<template>
    <CompassPanel
        class="panel-create-phonecall"
        :modelValue="displayPanel"
        :title="panelTitle"
        icon="obj-slsy-phone"
        :primaryAction="primaryAction"
        :cancelButtonProps="cancelButtonProps"
        @closed="onClosePanel"
    >
        <ActivityDetailPanelContentLoadingState v-if="isLoading" />
        <template v-else>
            <h3 class="panel-create-phonecall__content-title typographies-heading-h3">
                {{ localizedTexts.contentTitle }}
            </h3>
            <div class="panel-create-phonecall__form">
                <CompassDescriptionList v-if="!isDone" type="alternative">
                    <CompassDescriptionListItem :label="localizedTexts.priority">
                        <ActivitiesPriorityIconButton
                            :isActive="activityIsPrioritized"
                            enabled
                            @clicked="togglePriority"
                        />
                    </CompassDescriptionListItem>
                </CompassDescriptionList>
                <CompassFormItem
                    v-if="isDone"
                    :label="localizedTexts.source"
                    orientation="horizontal"
                    size="small"
                >
                    <CompassRadioGroup
                        v-model="activityFormData.source"
                        label=""
                        direction="horizontal"
                    >
                        <CompassRadio
                            v-for="source in metas.phonecall.sources"
                            :key="source.id"
                            :modelValue="source.id"
                            :label="source.label"
                        />
                    </CompassRadioGroup>
                </CompassFormItem>
                <CompassFormItem
                    :label="localizedTexts.label"
                    orientation="horizontal"
                    size="small"
                >
                    <CompassSelect
                        v-model="activityFormData.topic"
                        filterable
                        menuClass="phonecall-form__select-menu"
                    >
                        <CompassDropdownOption
                            v-for="topic in topicsOptions"
                            :key="topic.id"
                            :label="topic.label"
                            :value="topic.id"
                            type="text"
                        />
                    </CompassSelect>
                </CompassFormItem>

                <ActivityFormPanelDatePhoneCall
                    :activityDate="activityFormData.date"
                    :workingDays="metas.working_days"
                    :workingHours="metas.working_hours"
                    :activityData="activityData"
                    :isDone="isDone"
                    @update:activityDate="(value) => (activityFormData.date = value)"
                />
                <CompassFormItem
                    :label="$t.__('common.labels.assignee')"
                    size="small"
                    orientation="horizontal"
                    :disabled="!canUpdateAssignee"
                >
                    <CompassSelect
                        v-model="activityFormData.owner_id"
                        menuClass="phonecall-form__select-menu"
                        :disabled="!canUpdateAssignee"
                    >
                        <CompassDropdownOption
                            v-for="staff in staffFiltered"
                            :key="staff.id"
                            :value="staff.id"
                            :label="getFullNameUser(staff)"
                            :avatar="formatUserToCompassAvatar(staff)"
                            type="avatar"
                            :disabled="!canUpdateAssignee"
                        />
                    </CompassSelect>
                </CompassFormItem>
                <CompassFormItem
                    v-if="shouldDisplaySelectContact"
                    :label="$t.__('common.labels.contact')"
                    orientation="horizontal"
                    size="small"
                >
                    <CompassSelect
                        v-model="contactId"
                        menuClass="phonecall-form__select-menu"
                        :placeholder="$t.__('common.labels.add_contact')"
                    >
                        <CompassDropdownOption
                            v-for="contactFetched in contactsFetched"
                            :key="contactFetched.id"
                            :label="getFullNameUser(contactFetched)"
                            :avatar="formatUserToCompassAvatar(contactFetched)"
                            :value="contactFetched.id"
                            type="avatar"
                        />
                    </CompassSelect>
                </CompassFormItem>
                <RichText
                    v-model="activityFormData.description"
                    class="description-input-max-height"
                    :options="optionsRichText"
                    :formitem="{
                        size: 'small',
                        label: $t.__('common.labels.note'),
                    }"
                />
                <CompassFormItem
                    v-if="isDone"
                    :label="localizedTexts.phonecall.duration"
                    orientation="horizontal"
                    size="small"
                >
                    <DurationInput v-model="durationInput" placeholder="00:00:00" />
                </CompassFormItem>
                <CompassFormItem
                    v-if="isDone"
                    :label="localizedTexts.phonecall.result"
                    orientation="horizontal"
                    size="small"
                >
                    <CompassSelect
                        v-model="activityFormData.result"
                        menuClass="phonecall-form__select-menu"
                    >
                        <CompassDropdownOption
                            v-for="result in resultOptions"
                            :key="result.id"
                            :label="result.label"
                            :value="result.id"
                        />
                    </CompassSelect>
                </CompassFormItem>
                <CompassAccordion
                    v-if="!isDone"
                    :title="$t.__('common.labels.advanced_settings')"
                    open
                    class="panel-create-phonecall__form-advanced-settings"
                >
                    <ActivityFormPanelReminder
                        :reminders="activityFormData.reminders"
                        :isEditMode="!!activityData"
                        @updateActivityReminder="
                            (reminders) => (activityFormData.reminders = reminders)
                        "
                    />
                </CompassAccordion>
            </div>
        </template>
    </CompassPanel>
</template>

<script>
import RichText from 'vcomponents/richtext/RichText.vue';
import ActivityDetailPanelContentLoadingState from 'vcomponents/activities/detailPanel/ActivityDetailPanelContentLoadingState.vue';
import ActivityFormPanelDatePhoneCall from 'vcomponents/activities/activitiesFormPanel/ActivityFormPanelDatePhoneCall.vue';
import DurationInput from 'vcomponents/inputs/DurationInput.vue';
import ActivityFormPanelReminder from 'vcomponents/activities/activitiesFormPanel/ActivityFormPanelReminder.vue';
import ActivitiesPriorityIconButton from 'vcomponents/activities/priorityIconButton/ActivitiesPriorityIconButton.vue';
import { fetchActivitiesCrmMetas } from 'api/activities/activitiesService.js';
import ActivityFormService from 'api/activities/activityFormService.js';
import { getAllContactsForCompanyOrIndividual } from 'api/contacts/contactsService.js';
import ActivityFormMixins from 'mixins/activities/ActivityFormMixins.js';
import { formatUserToCompassAvatar, getFullNameUser } from 'Helpers/avatarHelper';
import { arrayToObject } from 'Helpers/arrayToObjectHelper.js';
import { createNamespacedHelpers } from 'vuex';
import ActivitiesStore from 'stores/activitiesStore.js';
import * as Sentry from '@sentry/vue';
import { addBrToEmptyPragraphs } from 'Helpers/richTextHelper.js';

const popupPanelMapActions = createNamespacedHelpers('popupPanel').mapActions;
const activitiesStoreMapActions = createNamespacedHelpers('activitiesStores').mapActions;
const userMapState = createNamespacedHelpers('user').mapState;

export default {
    components: {
        ActivityFormPanelReminder,
        DurationInput,
        ActivityDetailPanelContentLoadingState,
        ActivityFormPanelDatePhoneCall,
        ActivitiesPriorityIconButton,
        RichText,
    },
    mixins: [ActivityFormMixins],
    props: {
        id: {
            type: Number,
            default: null,
        },
        related: {
            type: Object,
            default: null,
        },
        callback: {
            type: Function,
            default: null,
        },
        scheduled: {
            type: Boolean,
            default: false,
        },
    },
    data() {
        return {
            isLoading: true,
            displayPanel: false,
            topicsOptions: [],
            durationInput: '',
            activityFormData: {
                owner_id: null,
                topic: 0,
                result: '',
                source: 'outcoming',
                related: this.related ? [this.related] : [],
                duration: 0,
                date: null,
                description: '',
                priority: 0,
            },
            saveIsPending: false,
            activityData: null,
            activityFormService: new ActivityFormService('phonecall'),
            contactsFetched: [],
            contactId: 0,
            metas: null,
            optionsRichText: [
                'Bold',
                'Italic',
                'Underline',
                'BulletList',
                'OrderedList',
                'TextAlign',
                'Divider',
                'Link',
            ],
        };
    },
    computed: {
        ...userMapState({ idCurrentUser: 'id', isAdmin: 'isAdmin' }),
        panelTitle() {
            if (this.id && this.activityData) {
                return this.$l10n.__(`Appel - ${this.activityData.result.name}`);
            } else if (!this.isLoading) {
                return this.isDone
                    ? this.localizedTexts.phonecall.titleCallDone
                    : this.localizedTexts.phonecall.titleCallScheduled;
            }
            return this.$l10n.__('Appel');
        },
        isDone() {
            if (this.activityData) {
                return this.activityData.result.id !== 'scheduled';
            }
            return !this.scheduled;
        },
        resultOptions() {
            return this.metas.phonecall.statuses.filter((status) => status.id !== 'scheduled');
        },
        shouldDisplaySelectContact() {
            if (this.related && this.related?.type === 'people') {
                return false;
            }
            return (
                (this.activityFormData.related?.length === 1 && !this.hasContactInRelated) ||
                this.activityFormData.related?.length > 1
            );
        },
        hasContactInRelated() {
            return this.activityFormData.related?.some((item) => item.type === 'contact');
        },
        staffFiltered() {
            if (this.activityData) {
                return this.metas.authors;
            }
            return this.metas.authors.filter((staff) => staff.status === 'ok');
        },
        canUpdateAssignee() {
            if (!this.activityData) {
                return true;
            }
            if (this.activityData?.owner?.id === this.idCurrentUser) {
                return true;
            }
            return this.isAdmin;
        },
        primaryAction() {
            return {
                label: this.localizedTexts.saveBtn,
                loading: this.saveIsPending,
                action: this.onSave,
            };
        },
        cancelButtonProps() {
            return {
                label: this.localizedTexts.cancelBtn,
                disabled: this.saveIsPending,
            };
        },
    },
    watch: {
        $route() {
            this.onClosePanel();
        },
        scheduled: {
            async handler() {
                await this.initPanel();
            },
        },
    },
    created() {
        if (!this.$store.state.activitiesStores) {
            this.$store.registerModule('activitiesStores', ActivitiesStore);
        }
        if (this.related?.type === 'individual' || this.related?.type === 'company') {
            this.contactId = Number(this.$store.getters.mainContact?.peopleId ?? 0);
        }
        if (this.related?.type === 'opportunity') {
            const mainContact = arrayToObject(this.$store.getters.mainContact);
            this.contactId = Number(mainContact?.peopleId ?? 0);
        }
    },
    async mounted() {
        await this.initPanel();
    },
    beforeUnmount() {
        if (this.$store.getters['activitiesStores/getActivityDataPanel']) {
            this.$store.dispatch('activitiesStores/updateActivityDataPanel', null);
        }
    },
    methods: {
        ...popupPanelMapActions(['close']),
        ...activitiesStoreMapActions(['updateMetasData']),
        formatUserToCompassAvatar,
        getFullNameUser,
        async initPanel() {
            try {
                this.isLoading = true;
                this.displayPanel = true;
                this.activityFormData.owner_id = this.idCurrentUser;
                this.metas = this.$store.getters['activitiesStores/getMetas'];

                if (!this.metas) {
                    const { data: metas } = await fetchActivitiesCrmMetas();
                    this.metas = metas;
                }
                this.updateMetasData(this.metas);

                this.topicsOptions = this.metas.phonecall.labels;
                const defaultTopic = this.topicsOptions.find((topic) => topic.is_default);
                this.activityFormData.topic = defaultTopic?.id ?? this.topicsOptions[0]?.id;
                this.activityFormData.result = this.resultOptions[0]?.id ?? null;
                if (!this.isDone) {
                    this.activityFormData.result = 'scheduled';
                }

                if (this.id) {
                    let data = this.$store.getters['activitiesStores/getActivityDataPanel'];
                    if (!data) {
                        data = await this.activityFormService.getActivity(this.id);
                    }
                    this.activityData = data;
                    if (data.duration !== 0) {
                        this.durationInput = this.formatDurationInputFromSeconds(data.duration);
                    }
                    const reminders =
                        data.reminders?.map(({ medium, delay }) => ({ medium, delay })) ?? [];
                    this.activityFormData = {
                        ...this.activityFormData,
                        ...data,
                        description: data.description ?? '',
                        owner_id: data.owner.id,
                        topic: data.topic.id,
                        result: data.result.id,
                        reminders,
                    };

                    if (
                        !this.activityFormData.topic ||
                        !this.topicsOptions.some(({ id }) => id === this.activityFormData.topic)
                    ) {
                        this.activityFormData.topic = defaultTopic?.id ?? this.topicsOptions[0]?.id;
                    }

                    this.contactId = this.activityFormData.contact_id ?? 0;
                }
                if (this.shouldDisplaySelectContact) {
                    await this.fetchContacts();
                }
            } catch (e) {
                Sentry.captureException(e);
                console.error(e);
                this.$pushToast({
                    type: 'error',
                    text: this.$l10n.__(
                        'Une erreur est survenue lors de la récupération des données',
                    ),
                    icon: true,
                });
                this.onClosePanel();
            } finally {
                this.isLoading = false;
            }
        },
        onClosePanel() {
            if (this.$store.state.activitiesStores) {
                this.$store.dispatch('activitiesStores/updateActivityDataPanel', null);
                this.$store.dispatch('activitiesStores/updateActivitySelected', null);
            }
            this.$emit('close');
        },
        formatDurationInputFromSeconds(duration) {
            // We have to get hour from duration
            let hours = Math.floor(duration / 3600);

            // We have to get the rest of the duration without hours
            duration -= hours * 3600;
            const now = moment().startOf('day').add(duration, 'second');

            hours = hours.toString().length === 1 ? `0${hours}` : hours.toString();
            return `${hours}:${now.format('mm:ss')}`;
        },
        formatDurationFromInput() {
            if (!this.durationInput) {
                return 0;
            }

            const durationSplit = this.durationInput.split(':');

            const hours = +durationSplit[0] ?? 0;
            const minutes = +durationSplit[1] ?? 0;
            const seconds = +durationSplit[2] ?? 0;
            return hours * 3600 + minutes * 60 + seconds;
        },
        async onSave() {
            try {
                this.saveIsPending = true;
                if (this.contactId !== 0) {
                    if (this.hasContactInRelated) {
                        this.activityFormData.related = this.activityFormData.related.map(
                            (item) => {
                                if (item.type === 'contact') {
                                    return { type: 'contact', id: this.contactId };
                                }
                                return item;
                            },
                        );
                    } else {
                        this.activityFormData.related.push({ type: 'contact', id: this.contactId });
                    }
                }
                const description = addBrToEmptyPragraphs(this.activityFormData.description);
                const activity = {
                    ...this.activityFormData,
                    description,
                    duration: this.formatDurationFromInput(),
                };
                const promise = this.id
                    ? this.activityFormService.updateActivity(activity)
                    : this.activityFormService.createActivity(activity);
                const { data: data } = await promise;
                this.$pushToast({
                    type: 'success',
                    text: this.id
                        ? this.$l10n.__("L'activité a bien été mise à jour")
                        : this.$l10n.__('L’activité a bien été créée'),
                    icon: true,
                });
                if (this.callback) {
                    this.callback(data);
                    this.onClosePanel();
                    return;
                }
                this.onClosePanel();
            } catch (e) {
                Sentry.captureException(e);
                console.error(e);
                const text = this.id
                    ? this.$l10n.__("Une erreur est survenue lors de l'édition de l’activité")
                    : this.$l10n.__('Une erreur est survenue lors de la création de l’activité');
                this.$pushToast({
                    type: 'error',
                    text,
                    icon: true,
                });
                this.saveIsPending = false;
            }
        },
        async fetchContacts() {
            const informations = this.$store.getters.dataFetch;
            let isIndividual = !!this.activityFormData?.individual_id ?? false;
            let id = this.activityFormData.company_id ?? this.activityFormData.individual_id ?? 0;
            if (this.related) {
                isIndividual = this.related?.type === 'individual';
                id = this.related?.id;
                if (this.related?.type === 'opportunity') {
                    id = informations.third.find((index) => index.key === 'id').value;
                    const type = informations.third.find((index) => index.key === 'type').value;
                    isIndividual = type === 'person';
                }
            }

            this.contactsFetched = await getAllContactsForCompanyOrIndividual({
                isIndividual,
                id: +id,
            });
        },
    },
};
</script>

<style lang="scss">
.panel-create-phonecall {
    &__title {
        color: var(--colors-common-text-standard-on-color);
    }
    .fake-create-phonecall-title {
        width: 384px;
    }
    .fake-create-phonecall-object {
        width: 125px;
    }

    &__content-title {
        margin: 0 0 var(--spacing-3);
    }

    &__form {
        display: flex;
        flex-direction: column;
        gap: var(--spacing-4);

        .description-input-max-height {
            .ProseMirror {
                height: 260px;
                max-height: 260px;
            }
        }
        &-advanced-settings > .compass-accordion__content {
            display: flex;
            flex-direction: column;
            gap: var(--spacing-3);
        }
    }
    &__footer {
        display: flex;
        justify-content: end;
        padding: var(--spacing-3) var(--spacing-5);
        gap: var(--spacing-3);
        border-top: 1px solid var(--colors-common-border-default);
        background: var(--colors-common-background-default);
    }
}
</style>
<style lang="scss">
#compass-dropdown-container {
    .phonecall-form__select-menu {
        z-index: 1001;
    }
    .select-time-phonecall {
        width: fit-content;
    }
}
#compass-date-picker-container {
    .panel-create-activity__datepicker {
        z-index: 1001;
    }
}
</style>
